import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { BreadCrumb } from "primereact/breadcrumb";
import { ExtensionCategory, Graph, register } from "@antv/g6";
import { ReactNode } from "@antv/g6-extension-react";
import SmilesDrawer from "smiles-drawer";
import SectionHeading from "../../../../app/common/SectionHeading/SectionHeading";
import SmilesView from "../../../../app/common/SmilesView/SmilesView";
import agent from "../../../../app/api/agent";
import "./EvolutionViewG6.css";

// Register React node type once
register(ExtensionCategory.NODE, "react", ReactNode);

const COLORS = {
  optimize: { bg: "#e0e7ff", color: "#4338ca", label: "Optimize" },
  combine: { bg: "#d1fae5", color: "#047857", label: "Combine" },
  scaffold_hop: { bg: "#f3e8ff", color: "#7c3aed", label: "Scaffold Hop" },
};

// Simple molecule renderer for G6 nodes (no useNavigate dependency)
const SimpleMolecule = ({ smile, width, height }) => {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!canvasRef.current || !smile) return;
    const dpr = window.devicePixelRatio || 1;
    const cv = canvasRef.current;
    cv.width = width * dpr;
    cv.height = height * dpr;
    cv.style.width = width + "px";
    cv.style.height = height + "px";
    try {
      const drawer = new SmilesDrawer.Drawer({
        width: width * dpr, height: height * dpr,
        bondThickness: 0.8, fontSizeScale: 1.0,
      });
      SmilesDrawer.parse(smile, (tree) => {
        drawer.draw(tree, cv, "light", false);
      }, () => {});
    } catch (e) { console.error("SmilesDrawer error:", e); }
  }, [smile, width, height]);
  return <canvas ref={canvasRef} style={{ display: "block" }} />;
};

// React node component for molecular structure display
const MoleculeNode = ({ data }) => {
  const { smile, label, designId, nodeOpType, dimmed, searchActive } = data?.data || {};
  const opColor = COLORS[nodeOpType]?.color || "#6366f1";
  const opBg = COLORS[nodeOpType]?.bg || "#e0e7ff";
  const dimStyle = dimmed ? { opacity: 0.25, filter: "grayscale(1)" } : {};
  const nodeId = data?.id || "";
  return (
    <div data-node-id={nodeId}
      title={`ID: ${label || designId || nodeId}${smile ? `\nSMILES: ${smile}` : ""}`}
      style={{
        width: "100%", height: "100%", position: "relative",
        background: "#ffffff", borderRadius: 10,
        border: `3px solid ${opColor}`,
        boxShadow: `0 2px 8px ${opColor}33`,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        cursor: "pointer", overflow: "visible", boxSizing: "border-box",
        transition: "opacity 0.2s, filter 0.2s",
        ...dimStyle,
      }}>
      {/* Left connection dot */}
      <div data-action="connect-left" data-node-id={nodeId}
        style={{
          position: "absolute", left: -10, top: "50%", transform: "translateY(-50%)",
          width: 20, height: 20, borderRadius: "50%", background: opColor,
          border: "2px solid #fff", cursor: "crosshair", zIndex: 10,
          boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
        }} />
      {/* Right connection dot */}
      <div data-action="connect-right" data-node-id={nodeId}
        style={{
          position: "absolute", right: -10, top: "50%", transform: "translateY(-50%)",
          width: 20, height: 20, borderRadius: "50%", background: opColor,
          border: "2px solid #fff", cursor: "crosshair", zIndex: 10,
          boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
        }} />
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", width: "100%", minHeight: 0 }}>
        {smile ? (
          <SimpleMolecule smile={smile} width={380} height={280} />
        ) : (
          <span style={{ fontSize: 48, color: "#cbd5e1" }}>—</span>
        )}
      </div>
      <div style={{
        width: "100%", padding: "6px 0", textAlign: "center", flexShrink: 0,
        borderTop: `2px solid ${opBg}`,
        fontSize: 30, fontWeight: "bold", color: opColor,
      }}>
        {label || designId || "—"}
      </div>
    </div>
  );
};

const EvolutionViewG6 = observer(({ projectId }) => {
  const toast = useRef(null);
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);
  const graphRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailNode, setDetailNode] = useState(null);
  const [showNodeDetail, setShowNodeDetail] = useState(false);
  const [pinnedNodeId, setPinnedNodeId] = useState(null);
  const [detailEdge, setDetailEdge] = useState(null);
  const [showEdgeDetail, setShowEdgeDetail] = useState(false);
  const [edgeOperationType, setEdgeOperationType] = useState("optimize");
  const [designIdOptions, setDesignIdOptions] = useState([]);
  const [compoundLookup, setCompoundLookup] = useState({});
  const [allCompounds, setAllCompounds] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [evolutionParentId, setEvolutionParentId] = useState("");
  const [evolutionChildId, setEvolutionChildId] = useState("");
  const [evolutionOperation, setEvolutionOperation] = useState("optimize");
  const [evolutionChildMode, setEvolutionChildMode] = useState("existing");
  const [evolutionNewSmile, setEvolutionNewSmile] = useState("");
  const [evolutionNewDesignId, setEvolutionNewDesignId] = useState("");
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [evolutionFromDot, setEvolutionFromDot] = useState(null); // "left" | "right" | null

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try { const d = await agent.Compounds.subTable.list("evolution", projectId); setRows(d || []); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (projectId) {
      agent.Compounds.subTable.listDesignIds(projectId).then(setDesignIdOptions).catch(() => {});
      agent.Compounds.listByProject(projectId).then(list => {
        const all = list || [];
        setAllCompounds(all);
        const m = {};
        all.forEach(c => { if (c.designId) m[c.designId] = c.id; });
        setCompoundLookup(m);
      }).catch(() => {});
    }
  }, [projectId]);

  const graphData = useMemo(() => {
    // Build a designId → compound lookup for filling missing SMILES
    const compoundByDesign = {};
    allCompounds.forEach(c => {
      if (c.designId) compoundByDesign[c.designId] = c;
    });
    const nodeMap = new Map(); const edges = [];
    // First pass: collect operation types for child nodes (node color = operation that created it)
    const nodeOps = {};
    rows.forEach(r => {
      const cid = r.designId || r.externalCompoundIds || r.id;
      if (cid) nodeOps[cid] = nodeOps[cid] || (r.operationType || "optimize").toLowerCase();
    });
    rows.forEach(r => {
      const cid = r.designId || r.externalCompoundIds || r.id, pid = r.parentDesignId;
      if (cid && !nodeMap.has(cid)) {
        const ct = (r.operationType || "optimize").toLowerCase();
        nodeOps[cid] = nodeOps[cid] || ct;
        const sq = searchQuery.toLowerCase();
        const dimmed = sq && !cid.toLowerCase().includes(sq);
        nodeMap.set(cid, { id: cid, data: { label: cid, designId: r.designId, smile: r.canonicalSmile || r.smile, compoundId: r.compoundId, entityId: r.entityId, nodeOpType: nodeOps[cid] || "optimize", dimmed, searchActive: !!sq }, style: { size: [400, 340] } });
      }
      if (pid && !nodeMap.has(pid)) {
        const parentCompound = compoundByDesign[pid];
        const sq = searchQuery.toLowerCase();
        const dimmed = sq && !pid.toLowerCase().includes(sq);
nodeMap.set(pid, { id: pid, data: { label: pid, designId: pid, smile: parentCompound?.canonicalSmile || parentCompound?.smile || null, compoundId: r.parentCompoundId, entityId: r.parentEntityId, nodeOpType: nodeOps[pid] || "optimize", dimmed, searchActive: !!sq }, style: { size: [400, 340] } });
      }
      if (pid && cid) {
        const op = r.operationType || "optimize", s = COLORS[op.toLowerCase()] || COLORS.optimize;
        edges.push({
          source: pid, target: cid, id: `e-${pid}-${cid}-${r.id}`,
          data: { operationType: op, evolutionId: r.id, extraData: r.extraData, createdAt: r.createdAt },
          style: {
            stroke: s.color, lineWidth: 2, endArrow: true,
            labelText: s.label, labelFontSize: 10, labelFontWeight: 600, labelFill: s.color,
            labelBackground: true, labelBackgroundFill: s.bg, labelBackgroundPadding: [2, 6, 2, 6],
          },
        });
      }
    });
    return { nodes: Array.from(nodeMap.values()), edges };
  }, [rows, allCompounds, searchQuery]);

  // ── Fill remaining screen height ──
  const wrapperRef = useRef(null);
  const headerRef = useRef(null);
  const legendRef = useRef(null);
  const [canvasHeight, setCanvasHeight] = useState(500);
  useEffect(() => {
    const update = () => {
      if (wrapperRef.current) {
        const headerH = headerRef.current?.offsetHeight || 80;
        const legendH = legendRef.current?.offsetHeight || 36;
        const h = window.innerHeight - wrapperRef.current.getBoundingClientRect().top - headerH - legendH - 12;
        setCanvasHeight(Math.max(300, h));
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    if (loading || !containerRef.current || graphData.nodes.length === 0) return;
    if (graphRef.current) { graphRef.current.destroy(); graphRef.current = null; }

    const container = containerRef.current;
    const graph = new Graph({
      container,
      data: graphData,
      node: {
        type: "react",
        style: {
          size: [400, 340],
          component: (d) => <MoleculeNode data={d} />,
        },
      },
      edge: {
        type: "cubic-horizontal",
        style: {
          lineWidth: 2,
          labelFontSize: 10,
          labelFontWeight: 600,
          labelBackground: true,
          labelBackgroundPadding: [2, 6, 2, 6],
        },
      },
      layout: {
        type: "dagre",
        rankdir: "LR",
        nodesep: 60,
        ranksep: 280,
      },
      behaviors: ["drag-element", "zoom-canvas", "drag-canvas", {
        type: "create-edge",
        trigger: "click",
        enable: () => !!window.__g6DotClick,
        onCreate: (edge) => {
          const srcId = window.__g6DotClick;
          window.__g6DotClick = false;
          window.__g6BlockClick = true;
          setTimeout(() => { window.__g6BlockClick = false; }, 300);
          const src = edge?.source;
          const tgt = edge?.target;
          if (src && tgt) {
            if (src === tgt) {
              // Same node → create new child molecule
              setTimeout(() => {
                setEvolutionParentId(src);
                setEvolutionChildId("");
                setEvolutionChildMode("new");
                setEvolutionOperation("optimize");
                setEvolutionFromDot("connect-right");
                setShowAddDialog(true);
              }, 100);
            } else {
              const exists = graphData.edges.some(ed => ed.source === src && ed.target === tgt);
              if (exists) {
                toast.current?.show?.({ severity: "info", summary: "Relationship already exists", life: 2000 });
              } else {
                setTimeout(() => {
                  setEvolutionParentId(src);
                  setEvolutionChildId(tgt);
                  setEvolutionChildMode("existing");
                  setEvolutionOperation("optimize");
                  setEvolutionFromDot("connect-right");
                  setShowAddDialog(true);
                }, 100);
              }
            }
          }
          return false;
        },
      }],
      autoFit: { type: "view", padding: 40 },
      plugins: [{
        type: "toolbar",
        position: "top-right",
        getItems: () => [
          { id: "zoom-in", value: "zoom-in", title: "Zoom In" },
          { id: "zoom-out", value: "zoom-out", title: "Zoom Out" },
          { id: "auto-fit", value: "auto-fit", title: "Fit View" },
          { id: "reset", value: "reset", title: "Reset" },
          { id: "request-fullscreen", value: "request-fullscreen", title: "Fullscreen" },
          { id: "export", value: "export", title: "Export" },
          { id: "undo", value: "re-layout", title: "Re-layout" },
        ],
        onClick: (value) => {
          const graph = graphRef.current;
          if (!graph) return;
          switch (value) {
            case "zoom-in": graph.zoomBy(1.25); break;
            case "zoom-out": graph.zoomBy(0.8); break;
            case "auto-fit": graph.fitView(); break;
            case "reset": graph.zoomTo(1); graph.fitCenter(); break;
            case "re-layout": graph.layout(); break;
            case "request-fullscreen": document.fullscreenElement ? document.exitFullscreen() : containerRef.current?.parentElement?.requestFullscreen(); break;
            case "export": {
              const canvas = containerRef.current?.querySelector("canvas");
              if (canvas) { const link = document.createElement("a"); link.download = "evolution.png"; link.href = canvas.toDataURL(); link.click(); }
              break;
            }
          }
        },
      }],
    });

    // Register edge click handler BEFORE render
    graph.on("edge:click", (evt) => {
      const targetId = evt?.target?.id;
      if (!targetId) return;
      try {
        const items = graph.getEdgeData([targetId]);
        if (items?.length) {
          setDetailEdge(items[0]);
          setEdgeOperationType(items[0].data?.operationType || "optimize");
          setShowEdgeDetail(true);
          return;
        }
      } catch (e) {}
      const match = graphData.edges.find(e => targetId === e.id || targetId.includes(e.id) || e.id.includes(targetId));
      if (match) {
        setDetailEdge(match);
        setEdgeOperationType(match.data?.operationType || "optimize");
        setShowEdgeDetail(true);
      }
    });

    graph.render();
    graphRef.current = graph;

    // Listen for node click via delegated DOM events
    let clickStartX = 0, clickStartY = 0;
    // Set flag when clicking on a connection dot → create-edge behavior handles it
    container.addEventListener("mousedown", (e) => {
      clickStartX = e.clientX;
      clickStartY = e.clientY;
      const dot = e.target?.closest?.("[data-action^='connect-']");
      if (dot) {
        window.__g6DotClick = dot.getAttribute("data-node-id");
      }
    });
    // Normal click → detail dialog (skip if create-edge is active)
    container.addEventListener("click", (e) => {
      if (window.__g6DotClick || window.__g6BlockClick) return;
      const dx = e.clientX - clickStartX;
      const dy = e.clientY - clickStartY;
      if (Math.sqrt(dx * dx + dy * dy) > 5) return;
      const nodeEl = e.target?.closest?.("[data-node-id]");
      if (nodeEl) {
        const id = nodeEl.getAttribute("data-node-id");
        if (!pinnedNodeId || pinnedNodeId === id) {
          const nodeData = graphData.nodes.find(n => n.id === id);
          if (nodeData) {
            setDetailNode({ ...nodeData.data, id: nodeData.id });
            setShowNodeDetail(true);
          }
        }
      }
    });

    const ro = new ResizeObserver(() => {
      if (graphRef.current && !graphRef.current.destroyed) {
        graphRef.current.resize(container.scrollWidth || 800, container.scrollHeight || 500);
      }
    });
    ro.observe(container);

    // Listen for edge click via G6 event
    graph.on("edge:click", (evt) => {
      // evt.target.id should be the edge's model ID
      const targetId = evt?.target?.id;
      if (!targetId) return;
      // Try G6 API first
      try {
        const items = graph.getEdgeData([targetId]);
        if (items?.length) {
          setDetailEdge(items[0]);
          setEdgeOperationType(items[0].data?.operationType || "optimize");
          setShowEdgeDetail(true);
          return;
        }
      } catch (e) {}
      const match = graphData.edges.find(e => targetId === e.id || targetId.includes(e.id) || e.id.includes(targetId));
      if (match) {
        setDetailEdge(match);
        setEdgeOperationType(match.data?.operationType || "optimize");
        setShowEdgeDetail(true);
      }
    });

    // Listen for canvas click → "create new child" if edge creation was active
    graph.on("canvas:click", () => {
      const srcId = window.__g6DotClick;
      if (srcId) {
        window.__g6DotClick = false;
        window.__g6BlockClick = true;
        setTimeout(() => { window.__g6BlockClick = false; }, 300);
        setTimeout(() => {
          setEvolutionParentId(srcId);
          setEvolutionChildId("");
          setEvolutionChildMode("new");
          setEvolutionOperation("optimize");
          setEvolutionFromDot("connect-right");
          setShowAddDialog(true);
        }, 100);
      }
    });

    return () => {
      ro.disconnect();
      if (graphRef.current && !graphRef.current.destroyed) { graphRef.current.destroy(); graphRef.current = null; }
    };
  }, [graphData, loading, pinnedNodeId]);

  const handlePin = () => setPinnedNodeId(p => p === detailNode?.id ? null : (detailNode?.id || null));

  const handleEdgeSave = async () => {
    const evolutionId = detailEdge?.data?.evolutionId;
    if (!evolutionId) return;
    try {
      await agent.Compounds.subTable.save("evolution", {
        id: evolutionId,
        evolutionOperationType: edgeOperationType,
      });
      toast.current.show({ severity: "success", summary: "Updated", life: 2000 });
      setShowEdgeDetail(false);
      setDetailEdge(null);
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.title || err?.response?.data || err?.message || "Unknown error";
      toast.current.show({ severity: "error", summary: "Update failed", detail: typeof detail === "string" ? detail : JSON.stringify(detail), life: 6000 });
    }
  };

  const handleEdgeDelete = async () => {
    const evolutionId = detailEdge?.data?.evolutionId;
    if (!evolutionId) return;
    if (!window.confirm("Delete this evolution relationship?")) return;
    try {
      await agent.Compounds.subTable.delete("evolution", evolutionId);
      toast.current.show({ severity: "success", summary: "Deleted", life: 2000 });
      setShowEdgeDetail(false);
      setDetailEdge(null);
      fetchData();
    } catch (err) {
      toast.current.show({ severity: "error", summary: "Delete failed", detail: err?.response?.data?.title || err?.message, life: 4000 });
    }
  };

  const handleExport = () => {
    try {
      const canvas = containerRef.current?.querySelector("canvas");
      if (canvas) {
        const link = document.createElement("a");
        link.download = `evolution-${projectId}.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
        toast.current.show({ severity: "success", summary: "Exported", life: 2000 });
      }
    } catch (e) {
      toast.current.show({ severity: "error", summary: "Export failed", life: 3000 });
    }
  };

  const handleAddRow = async () => {
    if (!evolutionParentId) { toast.current.show({ severity: "warn", summary: "Parent required", life: 2000 }); return; }
    if (evolutionChildMode === "existing" && !evolutionChildId) { toast.current.show({ severity: "warn", summary: "Child required", life: 2000 }); return; }
    if (evolutionChildMode === "new" && (!evolutionNewDesignId || !evolutionNewSmile)) { toast.current.show({ severity: "warn", summary: "New child ID and SMILES required", life: 2000 }); return; }
    setSaving(true);
    try {
      const p = { projectId, evolutionOperationType: evolutionOperation };
      p.evolutionParentId = compoundLookup[evolutionParentId] || null;
      if (!p.evolutionParentId) { toast.current.show({ severity: "error", summary: "Parent not found", life: 3000 }); setSaving(false); return; }
      if (evolutionChildMode === "existing") { p.compoundId = compoundLookup[evolutionChildId] || null; if (!p.compoundId) { toast.current.show({ severity: "error", summary: "Child not found", life: 3000 }); setSaving(false); return; } }
      else { p.smile = evolutionNewSmile.trim(); p.designId = evolutionNewDesignId.trim(); p.externalCompoundIds = evolutionNewDesignId.trim(); }
      await agent.Compounds.subTable.save("evolution", p);
      toast.current.show({ severity: "success", summary: "Added", life: 2000 }); setShowAddDialog(false); resetAddForm(); fetchData();
    } catch (err) { toast.current.show({ severity: "error", summary: "Failed", detail: err?.response?.data?.title || err?.message, life: 4000 }); } finally { setSaving(false); }
  };

  const resetAddForm = () => { setEvolutionParentId(""); setEvolutionChildId(""); setEvolutionOperation("optimize"); setEvolutionChildMode("existing"); setEvolutionNewSmile(""); setEvolutionNewDesignId(""); setEvolutionFromDot(null); };
  const handleImportCsv = async (e) => { const f = e.target.files?.[0]; if (!f) return; try { const r = await agent.Compounds.subTable.importCsv("evolution", projectId, f); toast.current.show({ severity: "success", summary: `Imported ${typeof r === "number" ? r : r?.value ?? r} rows`, life: 3000 }); fetchData(); } catch (err) { toast.current.show({ severity: "error", summary: "Import failed", detail: err?.response?.data?.title || err?.message, life: 4000 }); } finally { if (fileInputRef.current) fileInputRef.current.value = ""; } };

  const nodeRelations = useMemo(() => {
    if (!detailNode) return { asParent: [], asChild: [] };
    const p = [], c = [];
    rows.forEach(r => { const ci = r.designId || r.externalCompoundIds || r.id, pi = r.parentDesignId; const op = r.operationType || "optimize", s = COLORS[op.toLowerCase()] || COLORS.optimize; if (pi === detailNode.id) p.push({ child: ci, op: s.label, opColor: s.color }); if (ci === detailNode.id && pi) c.push({ parent: pi, op: s.label, opColor: s.color }); });
    return { asParent: p, asChild: c };
  }, [detailNode, rows]);

  return (
    <div ref={wrapperRef} className="flex flex-column w-full evolution-wrapper" style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
      <Toast ref={toast} />
      <div ref={headerRef}>
        <div className="flex w-full pb-2"><BreadCrumb model={[{ label: "Compounds" }, { label: "Evolution (G6)" }]} /></div>
        <div className="flex w-full align-items-center" style={{ position: "relative" }}>
          <div style={{ flex: 1 }}>
            <SectionHeading icon="icon icon-common icon-math" heading="🔗 Evolution" color="#1e1b4b" textColor="#ffffff"
              customButtons={[
                { label: "Add Relationship", icon: "pi pi-plus", action: () => setShowAddDialog(true), className: "p-button-sm evolution-toolbar-btn" },
                { label: "Import CSV", icon: "pi pi-upload", action: () => fileInputRef.current?.click(), className: "p-button-sm evolution-toolbar-btn" },
                { label: "Export PNG", icon: "pi pi-download", action: handleExport, className: "p-button-sm evolution-toolbar-btn" },
              ]}
            />
          </div>
          <div className="flex align-items-center gap-2" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", zIndex: 10 }}>
            <span className="p-input-icon-left">
              <i className="pi pi-search" style={{ left: "0.75rem", color: "#94a3b8", fontSize: "0.85rem" }} />
              <InputText
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search ID..."
                style={{ width: 200, paddingLeft: "2rem", fontSize: "0.85rem", height: 32 }}
              />
            </span>
            {searchQuery ? (
              <span style={{ fontSize: "0.8rem", color: "#6366f1", fontWeight: 600, whiteSpace: "nowrap" }}>
                {graphData.nodes.filter(n => !n.data.dimmed).length}/{graphData.nodes.length}
              </span>
            ) : graphData.nodes.length > 0 ? (
              <span style={{ fontSize: "0.8rem", color: "#94a3b8", whiteSpace: "nowrap" }}>
                {graphData.nodes.length}n · {graphData.edges.length}e
              </span>
            ) : null}
          </div>
        </div>
      </div>
      <input ref={fileInputRef} type="file" accept=".csv" style={{ display: "none" }} onChange={handleImportCsv} />
      <div className="w-full evolution-g6-container" style={{ height: canvasHeight }}>
        {loading ? <div className="evolution-loading"><i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem", color: "#6366f1" }} /><span>Loading...</span></div>
        : graphData.nodes.length === 0 ? <div className="evolution-empty"><i className="pi pi-inbox" style={{ fontSize: "2.5rem", color: "#cbd5e1" }} /><span>No evolution relationships yet.</span></div>
        : <div ref={containerRef} className="evolution-g6-canvas" />}
      </div>
      <div ref={legendRef} className="flex gap-3 justify-content-center py-2 evolution-legend">
        {Object.entries(COLORS).map(([k, v]) => <div key={k} className="flex align-items-center gap-1"><span style={{ display: "inline-block", width: 20, height: 3, borderRadius: 2, background: v.color }} /><span style={{ fontSize: "0.75rem", color: "#64748b" }}>{v.label}</span></div>)}
        <div className="flex align-items-center gap-1"><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: "#e0e7ff", border: "2px solid #6366f1" }} /><span style={{ fontSize: "0.75rem", color: "#64748b" }}>Molecule</span></div>
      </div>

      <Dialog header={`🔬 ${detailNode?.label || ""}`} visible={showNodeDetail} style={{ width: "460px" }} modal draggable={false}
        onHide={() => { if (!pinnedNodeId) { setShowNodeDetail(false); setDetailNode(null); } }}
        footer={<div className="flex gap-2 justify-content-between" style={{ padding: "4px 0", width: "100%" }}>
          <div className="flex gap-2">
            <Button label="Schedule Synthesis" icon="pi pi-arrow-right" className="p-button-sm p-button-success"
              onClick={async () => {
                const cid = compoundLookup[detailNode?.designId || detailNode?.id];
                if (!cid) { toast.current?.show?.({ severity: "warn", summary: "Compound not found in project", life: 2000 }); return; }
                try {
                  await agent.Compounds.subTable.save("synthesis", { compoundId: cid, projectId });
                  toast.current?.show?.({ severity: "success", summary: "Scheduled for Synthesis", life: 2000 });
                  setShowNodeDetail(false); setDetailNode(null); setPinnedNodeId(null);
                } catch (err) {
                  const msg = err?.data || err?.response?.data?.title || err?.message || "";
                  if (msg.includes("already been scheduled")) {
                    toast.current?.show?.({ severity: "warn", summary: "Already scheduled", detail: "This compound was already pushed to Synthesis", life: 3000 });
                  } else {
                    toast.current?.show?.({ severity: "error", summary: "Failed", detail: msg, life: 4000 });
                  }
                }
              }} />
          </div>
          <div className="flex gap-2">
            <Button label={pinnedNodeId === detailNode?.id ? "Unpin" : "Pin"} icon={pinnedNodeId === detailNode?.id ? "pi pi-lock-open" : "pi pi-lock"} className="p-button-text p-button-sm" onClick={handlePin} />
            <Button label="Close" icon="pi pi-times" className="p-button-text p-button-sm" onClick={() => { setShowNodeDetail(false); setDetailNode(null); setPinnedNodeId(null); }} />
          </div>
        </div>}>
        {detailNode && <div className="flex flex-column gap-3" style={{ padding: "4px 0" }}>
          <div className="flex justify-content-center">{detailNode.smile ? <SmilesView smiles={detailNode.smile} width={300} height={220} /> : <div style={{ width: 300, height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", background: "#f8fafc", borderRadius: 8 }}>No structure</div>}</div>
          <div className="evolution-prop-grid"><div className="evolution-prop"><span className="evolution-prop-label">Design ID</span><span className="evolution-prop-value">{detailNode.designId || detailNode.label || "—"}</span></div><div className="evolution-prop"><span className="evolution-prop-label">Entity ID</span><span className="evolution-prop-value">{detailNode.entityId || "—"}</span></div></div>
          <div style={{ padding: "8px 12px", background: "#f8fafc", borderRadius: 6, border: "1px dashed #cbd5e1", color: "#94a3b8", fontSize: "0.8rem", textAlign: "center" }}>（未来的 Activity & DMPK 数据）</div>
          <div><div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#374151", marginBottom: 6 }}>进化关系</div>
            {nodeRelations.asParent.length === 0 && nodeRelations.asChild.length === 0 && <div style={{ color: "#94a3b8", fontSize: "0.8rem", fontStyle: "italic" }}>No relationships</div>}
            {nodeRelations.asParent.map((r, i) => <div key={i} style={{ fontSize: "0.8rem", color: "#475569", padding: "2px 0", fontFamily: "monospace" }}>{detailNode.id} → {r.child}<span style={{ color: r.opColor, fontWeight: 600, marginLeft: 6, fontSize: "0.7rem" }}>({r.op})</span></div>)}
            {nodeRelations.asChild.map((r, i) => <div key={i} style={{ fontSize: "0.8rem", color: "#475569", padding: "2px 0", fontFamily: "monospace" }}>{r.parent} → {detailNode.id}<span style={{ color: r.opColor, fontWeight: 600, marginLeft: 6, fontSize: "0.7rem" }}>({r.op})</span></div>)}
          </div>
        </div>}
      </Dialog>

      <Dialog header="🔗 Evolution Relationship" visible={showEdgeDetail} style={{ width: "420px" }} modal draggable={false}
        onHide={() => { setShowEdgeDetail(false); setDetailEdge(null); }}
        footer={
          <div className="flex gap-2 justify-content-between" style={{ padding: "4px 0", width: "100%" }}>
            <Button label="Delete" icon="pi pi-trash" className="p-button-danger p-button-sm"
              onClick={handleEdgeDelete} />
            <div className="flex gap-2">
              <Button label="Cancel" icon="pi pi-times" className="p-button-text p-button-sm"
                onClick={() => { setShowEdgeDetail(false); setDetailEdge(null); }} />
              <Button label="Save" icon="pi pi-check" className="p-button-sm"
                onClick={handleEdgeSave} />
            </div>
          </div>
        }>
        {detailEdge && <div className="flex flex-column gap-3" style={{ padding: "4px 0" }}>
          <div style={{ fontSize: "0.85rem", color: "#475569" }}>
            <div className="flex align-items-center gap-2 mb-2" style={{ fontFamily: "monospace", fontSize: "0.9rem" }}>
              <span style={{ fontWeight: 600, color: "#4338ca" }}>{detailEdge.source}</span>
              <span style={{ color: "#94a3b8" }}>→</span>
              <span style={{ fontWeight: 600, color: "#4338ca" }}>{detailEdge.target}</span>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>Operation Type</label>
            <Dropdown value={edgeOperationType}
              options={[
                { label: "Optimize", value: "optimize" },
                { label: "Combine", value: "combine" },
                { label: "Scaffold Hop", value: "scaffold_hop" },
              ]}
              onChange={e => setEdgeOperationType(e.value)}
              style={{ width: "100%" }} />
          </div>
          <div style={{ fontSize: "0.8rem", color: "#94a3b8" }}>
            <div>Created: {detailEdge.data?.createdAt ? new Date(detailEdge.data.createdAt).toLocaleString() : "—"}</div>
            {detailEdge.data?.extraData && <div>Extra: {detailEdge.data.extraData}</div>}
          </div>
        </div>}
      </Dialog>

      <Dialog header={evolutionFromDot ? (evolutionFromDot === "connect-right" ? "→ Add Child" : "← Add Parent") : "🔗 Add Evolution Relationship"} visible={showAddDialog} style={{ width: "500px" }} modal draggable={false}
        onHide={() => { setShowAddDialog(false); resetAddForm(); setEvolutionFromDot(null); }}
        footer={<div className="flex gap-2 justify-content-end" style={{ padding: "4px 0" }}><Button label="Cancel" icon="pi pi-times" className="p-button-text p-button-sm" onClick={() => { setShowAddDialog(false); resetAddForm(); setEvolutionFromDot(null); }} /><Button label="Save" icon="pi pi-check" className="p-button-sm evolution-primary-btn" loading={saving} onClick={handleAddRow} /></div>}>
        <div className="flex flex-column gap-3" style={{ padding: "4px 0" }}>
          {evolutionFromDot && (
            <div style={{ fontSize: "0.85rem", color: "#6366f1", fontWeight: 600, padding: "6px 10px", background: "#eef2ff", borderRadius: 6, textAlign: "center" }}>
              {evolutionFromDot === "connect-right"
                ? `${evolutionParentId || "?"} → New Child`
                : `New Parent → ${evolutionChildId || "?"}`}
            </div>
          )}
          <div><label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>Parent Design ID *</label><Dropdown value={evolutionParentId} options={designIdOptions} onChange={e => setEvolutionParentId(e.value)} placeholder="Search Parent" filter showClear style={{ width: "100%" }} /></div>
          <div><label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>Child</label>
            <div className="flex gap-2 mb-2"><Button label="Select existing" className={`p-button-sm ${evolutionChildMode === "existing" ? "p-button-primary" : "p-button-outlined"}`} style={{ flex: 1, fontSize: "0.8rem" }} onClick={() => setEvolutionChildMode("existing")} /><Button label="Create new" className={`p-button-sm ${evolutionChildMode === "new" ? "p-button-primary" : "p-button-outlined"}`} style={{ flex: 1, fontSize: "0.8rem" }} onClick={() => setEvolutionChildMode("new")} /></div>
            {evolutionChildMode === "existing" ? <Dropdown value={evolutionChildId} options={designIdOptions} onChange={e => setEvolutionChildId(e.value)} placeholder="Search Child" filter showClear style={{ width: "100%" }} />
            : <div className="flex flex-column gap-2"><InputText value={evolutionNewDesignId} onChange={e => setEvolutionNewDesignId(e.target.value)} placeholder="New Design ID" style={{ width: "100%" }} /><InputText value={evolutionNewSmile} onChange={e => setEvolutionNewSmile(e.target.value)} placeholder="SMILES" style={{ width: "100%" }} /></div>}
          </div>
          <div><label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>Operation Type *</label><Dropdown value={evolutionOperation} options={[{ label: "Optimize", value: "optimize" }, { label: "Combine", value: "combine" }, { label: "Scaffold Hop", value: "scaffold_hop" }]} onChange={e => setEvolutionOperation(e.value)} style={{ width: "100%" }} /></div>
        </div>
      </Dialog>
    </div>
  );
});

export default EvolutionViewG6;
